import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { VCS_PROVIDERS } from "constants/vcs_providers";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import useComboBoxInputValueItem from "ds/components/ComboBox/useInputValueItem";
import { BaseSelectItem } from "ds/components/Select/types";
import { VcsProvider } from "types/generated";
import { checkWithMultipleVCSIntegrations } from "utils/vcs";

import RepositoryTooltip from "../RepositoryTooltip";
import { SourceCodeProjects, SourceCodeSettingsField } from "../types";
import { getTooltipAnalyticsProps } from "../utils";
import { GET_REPOSITORIES, GetRepositoriesGql } from "./gql";
import { parseRepository } from "./helpers";

const isValidValue = (value: string) => {
  const trimmedValue = value.trim();

  const [namespace, repository] = trimmedValue.split("/");

  return !!namespace && !!repository;
};

const INPUT_VALUE_ITEM_PROPS = {
  label: "Can't find your repository? Select this option to add it anyway.",
};

type SourceCodeRepositoryFieldProps = {
  analyticsVersion?: string;
  projectType: SourceCodeProjects;
};

const SourceCodeRepositoryField = ({
  analyticsVersion,
  projectType,
}: SourceCodeRepositoryFieldProps) => {
  const { control, setValue, watch, setError, clearErrors } =
    useFormContext<SourceCodeSettingsField>();
  const [notExistingRepoOption, setNotExistingRepoOption] = useState<BaseSelectItem | undefined>(
    undefined
  );

  const formValues = watch();

  const currentValue =
    formValues.namespace && formValues.repository
      ? `${formValues.namespace}/${formValues.repository}`
      : null;

  const withMultipleVCSIntegrations = checkWithMultipleVCSIntegrations(formValues.provider);

  const { loading, data, error } = useQuery<GetRepositoriesGql>(GET_REPOSITORIES, {
    variables: {
      provider: formValues.provider,
      vcsIntegrationId: withMultipleVCSIntegrations ? formValues.vcsIntegrationId : null,
    },
  });

  useEffect(() => {
    clearErrors("repository");
    const namespace = watch("namespace");
    const repository = watch("repository");
    const currentRepositoryValue =
      namespace && repository ? `${namespace}/${repository}` : undefined;

    if (data?.repositories && data.repositories.length) {
      const hasCurrentValueInList = data.repositories.some(
        (repo) => `${repo.namespace}/${repo.name}` === currentRepositoryValue
      );

      if (currentRepositoryValue && !hasCurrentValueInList) {
        setNotExistingRepoOption({
          value: currentRepositoryValue,
          label: currentRepositoryValue,
        });
      }

      if (!currentRepositoryValue && data.repositories.length) {
        const { namespace, name } = data.repositories[0] ?? { name: null, namespace: null };

        setValue("repository", name, { shouldValidate: true, shouldDirty: true });
        setValue("namespace", namespace, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [data?.repositories, setValue, clearErrors, watch]);

  useEffect(() => {
    if (error) {
      setError("repository", {
        type: "custom",
        message: error.message,
      });
      setValue("branch", "", { shouldDirty: true });
    }
  }, [error, setError, setValue]);

  const repositoriesOptions = useMemo(() => {
    if (error || !data?.repositories) {
      return [];
    }

    const repositoriesOptions = data?.repositories.map((repository) => ({
      value: `${repository.namespace}/${repository.name}`,
      label: `${repository.namespace}/${repository.name}`,
    }));

    return repositoriesOptions;
  }, [data?.repositories, error]);

  const options = useMemo(
    () => [...(notExistingRepoOption ? [notExistingRepoOption] : []), ...repositoriesOptions],
    [notExistingRepoOption, repositoriesOptions]
  );

  const handleChange = useCallback(
    (value: string | null) => {
      const [namespace, repository] = parseRepository(value || "");

      if (namespace !== formValues.namespace || repository !== formValues.repository) {
        setValue("namespace", namespace, { shouldValidate: true, shouldDirty: true });
        setValue("repository", repository, { shouldValidate: true, shouldDirty: true });
        setValue("branch", "", { shouldDirty: true });
      }
    },
    [setValue, formValues]
  );

  const gitlabWarning =
    !loading && formValues.provider === VCS_PROVIDERS.Gitlab && !repositoriesOptions.length;

  const onInputValueSelected = useCallback(
    (value: string) => {
      setNotExistingRepoOption({
        value,
        label: value,
      });
    },
    [setNotExistingRepoOption]
  );

  const { comboBoxProps } = useComboBoxInputValueItem({
    onInputValueSelected,
    shouldIncludeInputValueItem: isValidValue,
    options,
    inputValueItemProps: INPUT_VALUE_ITEM_PROPS,
  });

  if (formValues.provider === undefined) {
    return null;
  }

  return (
    <Controller
      name="repository"
      control={control}
      rules={{ required: "Repository is required" }}
      render={({ fieldState }) => (
        <Box direction="column" gap="medium">
          <ComboBox
            label="Repository"
            {...getTooltipAnalyticsProps("Source Code", "Repository", projectType, {
              provider: formValues.provider,
              version: analyticsVersion,
            })}
            tooltipInfo={
              <RepositoryTooltip
                vcsProvider={formValues.provider as VcsProvider}
                whitelistable
                projectType={projectType}
              />
            }
            error={(!loading && fieldState.error?.message) || undefined}
            value={currentValue}
            isLoading={loading}
            {...comboBoxProps}
            onChange={comboBoxProps.onChange(handleChange)}
          >
            {(item) => <ComboBoxDefaultItem id={item.value} {...item} />}
          </ComboBox>
          {gitlabWarning && (
            <Banner variant="warning">
              We are only able show repositories that you have maintainer-level access to.
            </Banner>
          )}
        </Box>
      )}
    />
  );
};

export default SourceCodeRepositoryField;
